import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function BookingSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | success | failed | expired
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 8;

    const poll = async () => {
      try {
        const { data } = await api.get(`/bookings/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(data);
        if (data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attempts += 1;
        if (attempts >= maxAttempts) {
          setStatus("failed");
          return;
        }
        setTimeout(poll, 2000);
      } catch (_) {
        if (!cancelled) setStatus("failed");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="pt-32 min-h-screen max-w-2xl mx-auto px-6 text-center">
      {status === "polling" && (
        <div data-testid="booking-polling">
          <Loader2 className="w-12 h-12 mx-auto animate-spin text-[#0A0A0A]" />
          <h1 className="font-display text-3xl font-bold mt-6">Confirmation du paiement…</h1>
          <p className="text-neutral-600 mt-2">Merci de patienter quelques secondes.</p>
        </div>
      )}
      {status === "success" && (
        <div data-testid="booking-success">
          <CheckCircle2 className="w-16 h-16 mx-auto text-[#166534]" />
          <h1 className="font-display text-4xl font-bold mt-6">Votre séjour est réservé !</h1>
          <p className="text-neutral-600 mt-3">
            Montant payé : <strong>{data && (data.amount_total / 100).toFixed(2)}€</strong>.
            Vous recevrez un email de confirmation et l'hôte vous contactera prochainement.
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/traveler" className="bg-[#0A0A0A] text-white px-6 py-3 font-semibold hover:bg-[#1B2A49] transition-colors">
              Mes réservations
            </Link>
            <Link to="/search" className="border-2 border-[#0A0A0A] px-6 py-3 font-semibold hover:bg-neutral-100 transition-colors">
              Continuer à explorer
            </Link>
          </div>
        </div>
      )}
      {(status === "failed" || status === "expired") && (
        <div data-testid="booking-failed">
          <XCircle className="w-16 h-16 mx-auto text-[#991B1B]" />
          <h1 className="font-display text-3xl font-bold mt-6">Paiement non finalisé</h1>
          <p className="text-neutral-600 mt-2">La session a expiré ou le paiement a échoué.</p>
          <Link to="/search" className="inline-block mt-6 bg-[#0A0A0A] text-white px-6 py-3 font-semibold hover:bg-[#1B2A49]">
            Retour aux annonces
          </Link>
        </div>
      )}
    </div>
  );
}
